// src/services/voteServices.js

import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api`;

const getStoredToken = () => {
  try {
    const directToken = localStorage.getItem("token");

    if (directToken) {
      return directToken;
    }

    const rawUserInfo = localStorage.getItem("userInfo");
    const userInfo = rawUserInfo ? JSON.parse(rawUserInfo) : null;

    return userInfo?.token || userInfo?.accessToken || "";
  } catch {
    return "";
  }
};

const getAuthConfig = () => {
  const token = getStoredToken();

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

/// ===========================
/// 📦 VOTES
/// ===========================

export const getAllVotes = async () => {
  try {
    const response = await axios.get(`${baseUrl}/vote/all`, getAuthConfig());

    if (response.status !== 200) {
      throw new Error("Erreur de récupération");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des votes :", error);
    return [];
  }
};

export const getVoteById = async (id) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/${id}`, getAuthConfig());

    if (response.status !== 200) {
      throw new Error("Erreur de récupération");
    }

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement du vote :", error);
    return null;
  }
};

export const getVoteResults = async (voteId) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/${voteId}/results`);
    return response.data;
  } catch (error) {
    console.error("Erreur récupération résultats vote :", error);
    return null;
  }
};

export const createVote = async (voteData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/create`,
      voteData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du vote :", error);
    return null;
  }
};

export const updateVote = async (id, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/vote/update/${id}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du vote :", error);
    return null;
  }
};

export const deleteVote = async (id) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/vote/delete/${id}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression du vote :", error);
    return null;
  }
};

/// ===========================
/// ❓ QUESTIONS
/// ===========================

export const addQuestionToVote = async (voteId, questionData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/${voteId}/questions`,
      questionData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout de la question :", error);
    return null;
  }
};

export const updateQuestion = async (questionId, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/question/update/${questionId}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la question :", error);
    return null;
  }
};

export const deleteQuestion = async (questionId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/question/delete/${questionId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de la question :", error);
    return null;
  }
};

/// ===========================
/// ✅ CHOICES
/// ===========================

export const addChoiceToQuestion = async (questionId, choiceData) => {
  try {
    const response = await axios.post(
      `${baseUrl}/question/${questionId}/choices`,
      choiceData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout du choix :", error);
    return null;
  }
};

export const updateChoice = async (choiceId, updatedData) => {
  try {
    const response = await axios.put(
      `${baseUrl}/choice/update/${choiceId}`,
      updatedData,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du choix :", error);
    return null;
  }
};

export const deleteChoice = async (choiceId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/choice/delete/${choiceId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression du choix :", error);
    return null;
  }
};

/// ===========================
/// 🧾 BALLOTS
/// ===========================

export const submitVote = async (votePayload) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/submit`,
      votePayload,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la soumission du vote :", error);
    return null;
  }
};

export const getUserBallots = async (userId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/ballots/${userId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des réponses :", error);
    return [];
  }
};

export const getUserBallotsForVote = async (userId, voteId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/ballots/${userId}/by-vote/${voteId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des réponses détaillées :", error);
    return [];
  }
};

/// ===========================
/// 🔐 TOKENS D'INVITATION
/// ===========================

export const generateTokens = async (voteId, users) => {
  try {
    const response = await axios.post(
      `${baseUrl}/vote/${voteId}/generate-tokens`,
      { users },
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la génération des tokens :", error);
    return [];
  }
};

export const checkToken = async (token) => {
  try {
    const response = await axios.get(`${baseUrl}/vote/check-token/${token}`);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la vérification du token :", error);
    return { valid: false };
  }
};

export const getVoteTokens = async (voteId) => {
  try {
    const response = await axios.get(
      `${baseUrl}/vote/${voteId}/tokens`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des tokens :", error);
    return [];
  }
};

export const deleteToken = async (tokenId) => {
  try {
    const response = await axios.delete(
      `${baseUrl}/vote/token/delete/${tokenId}`,
      getAuthConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression du token :", error);
    return null;
  }
};
